import { Grid } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import ProductDescription from './ProductDescription';
import ShipmentTypeDetails from './ShipmentTypeDetails';
import BoxList from './BoxList';
import TotalValues from './TotalValue';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import Actions from '../Actions';
import { getRates, setCreateShipmentCSP } from '../../../store/reducers/GetRates';
import { useDispatch, useSelector } from 'react-redux';
import { SHIPMENT_TYPES } from '../../GetRatesV2/Constant';
import { AddressType } from '../../../store/reducers/GetRates/Type';
import { AppDispatch, RootStateType } from '../../../store';
import NoCSPFound from '../../GetRatesV2/CSPList/NoCSPFound/NoCSPFound';
import ViewAction from '../../ViewSingleShipment/ViewAction';
import PackageAiValidationAlert from '../../../components/PackageAiValidationAlert';
import { useAiValidation } from '../../../hooks/useAiValidation';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';

function getAddressObj({ pincode, city, country, state }: AddressType) {
  return { city, state, country, pincode };
}

const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return '';
  if (typeof dutyCharge === 'string') return dutyCharge;
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type;
  return '';
};

function ShipmentDetails({
  removeShipmentInfo,
  showViewMoreAction,
  isShipmentDetailsWriteAllowed,
}: {
  removeShipmentInfo?: boolean;
  showViewMoreAction?: boolean;
  isShipmentDetailsWriteAllowed?: boolean;
}) {
  const [viewMore, setViewMore] = useState<boolean>(true);

  const dispatch = useDispatch<AppDispatch>();
  const {
    activeStep,
    setActiveStep,
    setShowRates,
    chargeableWeight,
    boxes,
    setSelectedBox,
    setSelectedProduct,
    consignee,
    shipper,
    totalShipmentValue: value,
    shipmentType,
    boxValidation,
    userId,
    pickupType: { selectedWearhouseAddress },
    pickupType,
    packageData,
    extraDetails,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const { validationStates, validate } = useAiValidation();

  const rates = useSelector((state: RootStateType) => state.getRates);
  const { chareableWeight = { maxChargeableWt: 0 } } = useSelector((state: RootStateType) => state.getRates);
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);
  const countriesNameObj = useSelector((state: RootStateType) => state.global.countriesNameObj) || {};
  const countries = useSelector((state: RootStateType) => state.global.countries) || [];

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '', _country } = organizationData || {};

  const orgCountryName = _country?.name?.trim() || '';

  const getCountryName = (field: any): string => {
    if (!field) return '';
    if (typeof field === 'string') return field.trim();
    if (typeof field === 'object') {
      if (typeof field.country === 'string') return field.country.trim();
      if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
        return field.country.name.trim();
      }
    }
    return '';
  };

  const shipperCountry = getCountryName(shipper);
  const consigneeCountry = getCountryName(consignee);

  let checkCountries: string[] = [];
  if (shipperCountry || consigneeCountry) {
    checkCountries = [shipperCountry, consigneeCountry].filter(Boolean);
  } else {
    checkCountries = [getCountryName(shipmentById?._shipperId), getCountryName(shipmentById?._consigneeId)].filter(
      Boolean,
    );
  }

  const isShipmentExport = orgCountryName
    ? checkCountries.some(c => c.toLowerCase() !== orgCountryName.toLowerCase())
    : false;

  console.log('chargeableWeight:', chargeableWeight);

  // Stable shipmentPackage validation payload builder
  const packagePayload = useMemo(() => {
    const purpose =
      extraDetails?.purposeOfShipment && String(extraDetails.purposeOfShipment).trim()
        ? String(extraDetails.purposeOfShipment).trim()
        : 'FOR_SALE';

    const insurance = typeof packageData?.insurance === 'boolean' ? packageData.insurance : false;
    const unitOfMeasurement = shipmentType?.unit || packageData?.unitOfMeasurement || 'kg-cm';
    const currency = shipmentType?.currency || packageData?.currency || 'INR';

    const boxPayload = (boxes || []).map(b => ({
      length: Number(b.length || 0),
      width: Number(b.width || 0),
      height: Number(b.height || 0),
      weight: Number(b.weight || 0),
      products: (b.products || []).map((p: any) => ({
        description: String(p.description || '').trim(),
        quantity: Number(p.quantity || 0),
        unitValue: Number(p.unitValue || 0),
        HSNName: p.HSNName ? String(p.HSNName).trim() : '',
        HSNCode: p.HSNCode ? String(p.HSNCode).trim() : '',
        productId: p.productId ? String(p.productId).trim() : '',
        productIdType: p.productIdType ? String(p.productIdType).trim() : 'SKU',
        merchantProductId: p.merchantProductId ? String(p.merchantProductId).trim() : '',
        standardManufacturerProductId: p.standardManufacturerProductId
          ? String(p.standardManufacturerProductId).trim()
          : '',
        nonStandardManufacturerProductId: p.nonStandardManufacturerProductId
          ? String(p.nonStandardManufacturerProductId).trim()
          : '',
      })),
    }));

    return {
      purposeOfShipment: purpose,
      insurance,
      unitOfMeasurement,
      currency,
      box: boxPayload,
    };
  }, [
    extraDetails?.purposeOfShipment,
    packageData?.insurance,
    packageData?.unitOfMeasurement,
    packageData?.currency,
    shipmentType?.unit,
    shipmentType?.currency,
    boxes,
  ]);

  // Trigger centralized AI package validation
  useEffect(() => {
    validate('shipmentPackage', 'shipmentPackage', packagePayload);
  }, [packagePayload, validate]);

  useEffect(() => {
    if (rates.redirectToCreateShipmentCSP) {
      setActiveStep(activeStep + 1);
      dispatch(setCreateShipmentCSP(false));
    }
  }, [rates.redirectToCreateShipmentCSP]);

  function handleGetRates() {
    if (isShipmentExport) {
      // Find the first product that is missing HSN Code
      let missingProduct: any = null;
      let missingBox: any = null;
      let missingProductIndex = -1;

      for (const box of boxes) {
        if (box.products) {
          const idx = box.products.findIndex(p => !p.HSNCode?.trim());
          if (idx !== -1) {
            missingProduct = box.products[idx];
            missingBox = box;
            missingProductIndex = idx;
            break;
          }
        }
      }

      if (missingProduct) {
        // 1. Select the box
        setSelectedBox(missingBox);
        // 2. Select the product to load it into the form for editing and show validation
        setSelectedProduct({
          ...missingProduct,
          id: missingProductIndex.toString(),
          isUpdate: true,
        });
        return; // Do not call the API
      }

      const getCountryCode = (countryName: string): string => {
        if (!countryName) return '';
        const trimmedName = countryName.trim();
        if (countriesNameObj[trimmedName]?.countryCode) {
          return countriesNameObj[trimmedName].countryCode;
        }
        const keys = Object.keys(countriesNameObj);
        const matchedKey = keys.find(k => k.toLowerCase() === trimmedName.toLowerCase());
        if (matchedKey && countriesNameObj[matchedKey]?.countryCode) {
          return countriesNameObj[matchedKey].countryCode;
        }
        const foundCountry = countries.find(c => {
          if (c.name?.toLowerCase() === trimmedName.toLowerCase()) return true;
          if (c.alternateNames?.some(alt => alt.toLowerCase() === trimmedName.toLowerCase())) return true;
          return false;
        });
        return foundCountry?.countryCode || '';
      };

      const sourceAddr = selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper;
      const destAddr = consignee;

      const internationalPayload = {
        destination: {
          city: destAddr.city || '',
          state: destAddr.state || '',
          country: destAddr.country || '',
          countryCode: getCountryCode(destAddr.country),
          pincode: destAddr.pincode || '',
        },
        source: {
          city: sourceAddr.city || '',
          state: sourceAddr.state || '',
          country: sourceAddr.country || '',
          countryCode: getCountryCode(sourceAddr.country),
          pincode: sourceAddr.pincode || '',
        },
        shipmentType: shipmentType.type,
        userId,
        weight: chareableWeight?.maxChargeableWt || 0,
        value,
        package: {
          box: boxes.flatMap(box => {
            const boxWeight = box.weight ? box.weight.toString() : '0';
            const boxLength = box.length ? box.length.toString() : '0';
            const boxWidth = box.width ? box.width.toString() : '0';
            const boxHeight = box.height ? box.height.toString() : '0';

            return {
              length: boxLength,
              width: boxWidth,
              height: boxHeight,
              weight: boxWeight,
              products: (box.products || []).map((product: any) => {
                const qtyVal =
                  product.quantity !== undefined && product.quantity !== null
                    ? Number(product.quantity)
                    : product.qty !== undefined && product.qty !== null
                    ? Number(product.qty)
                    : 0;
                const unitVal =
                  product.unitValue !== undefined && product.unitValue !== null
                    ? Number(product.unitValue)
                    : product.value !== undefined && product.value !== null
                    ? Number(product.value)
                    : 0;
                return {
                  hsnCode: product.HSNCode || product.hsnCode || '',
                  qty: qtyVal,
                  quantity: qtyVal,
                  value: unitVal,
                  unitValue: unitVal,
                  description: product.description || '',
                };
              }),
            };
          }),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        insurance: packageData.insurance,
        packaging: packageData.packaging,
        currency: shipmentType.currency,
        dropOff: pickupType.type === 'dropOff' ? true : false,
        organizationName: name === 'Cargo Solutions Network',
        rateFor: 'create-shipment' as const,
        ...(isDutyChargesAllowedSubdomain() &&
          getDutyChargeType(shipmentType.dutyCharge) && {
            dutyCharge: getDutyChargeType(shipmentType.dutyCharge),
          }),
      };

      dispatch(getRates([internationalPayload, false]));
    } else {
      // Domestic flow: keep completely unchanged
      const obj = {
        destination: { ...getAddressObj(consignee) },
        source: { ...getAddressObj(selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper) },
        shipmentType: shipmentType.type,
        userId,
        weight: chareableWeight?.maxChargeableWt || 0,
        value,
        package: {
          box: boxes.map(box => ({
            weight: box.weight ? box.weight.toString() : '0',
            length: box.length || 0,
            width: box.width || 0,
            height: box.height || 0,
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        insurance: packageData.insurance,
        packaging: packageData.packaging,
        dropOff: pickupType.type === 'dropOff' ? true : false,
        ...(isDutyChargesAllowedSubdomain() &&
          getDutyChargeType(shipmentType.dutyCharge) && {
            dutyCharge: getDutyChargeType(shipmentType.dutyCharge),
          }),
      };
      dispatch(
        getRates([{ ...obj, organizationName: name === 'Cargo Solutions Network', rateFor: 'create-shipment' }, false]),
      );
    }
  }

  return (
    <>
      <Grid container>
        {!removeShipmentInfo && <ShipmentTypeDetails />}
        <Grid container item alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <TotalValues />
          <PackageAiValidationAlert validationState={validationStates['shipmentPackage']} />
        </Grid>
        {showViewMoreAction && (
          <Grid container justifyContent="flex-end">
            <ViewAction setViewMore={setViewMore} viewMore={viewMore} />
          </Grid>
        )}
        {(viewMore || !showViewMoreAction) && (
          <Grid container className="my-3" spacing={2}>
            <Grid item lg={4} xl={3} md={4} sm={12} xs={12}>
              <BoxList writeAccess={isShipmentDetailsWriteAllowed} />
            </Grid>
            <Grid item lg={8} xl={9} md={8} sm={12} xs={12}>
              <ProductDescription writeAccess={isShipmentDetailsWriteAllowed} />
              {/* <DashedBtn classes="w-full" label={'Add a Product'} onClick={handleAddNewProduct} /> */}
            </Grid>
          </Grid>
        )}
      </Grid>
      {!removeShipmentInfo && <Actions onNext={handleGetRates} disableNext={!boxValidation} />}
      <NoCSPFound setShowRates={setShowRates} />
    </>
  );
}

export default ShipmentDetails;
